import styled from 'styled-components';
import React, { useState, useRef, useEffect } from 'react';
import { translate } from 'base/shared/Localization';

const Container = styled.div`
  background-color: #ffffff;
  padding: 4rem 0rem;
`;

const MainContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: #111827;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const FAQContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const QuestionsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const QuestionButton = styled.button`
  background: ${(props) => (props.isActive ? props.activeColor : '#f3f4f6')};
  color: ${(props) => (props.isActive ? '#ffffff' : '#6b7280')};
  border: none;
  border-radius: 0.75rem;
  padding: 1rem;
  text-align: left;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  line-height: 1.4;

  &:hover {
    background: ${(props) => (props.isActive ? props.activeColor : '#e5e7eb')};
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 1.25rem;
  }
`;

const AnswerColumn = styled.div`
  border-radius: 0.75rem;
  padding: 0rem 2rem;
  min-height: 300px;
  max-height: 600px;
  overflow-y: auto;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    min-height: 250px;
    max-height: 500px;
  }
`;

const AnswerContent = styled.div`
  color: #4b5563;
  font-size: 1rem;
  line-height: 1.6;

  p {
    margin-bottom: 1rem;
  }

  strong {
    color: #111827;
    font-weight: 600;
  }

  ul,
  ol {
    margin: 1rem 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
  }
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #9ca3af;
  font-size: 1.1rem;
`;

export default function FAQSection({ mode = 'all-rooms' }) {
  const [activeQuestion, setActiveQuestion] = useState(0);
  const answerRef = useRef(null);
  const mainContentRef = useRef(null);
  const shouldScroll = useRef(false);

  const handleQuestionClick = (id) => {
    if (id !== activeQuestion) {
      setActiveQuestion(id);
      shouldScroll.current = true;
    }
  };

  useEffect(() => {
    if (shouldScroll.current && mainContentRef.current) {
      const rect = mainContentRef.current.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      window.scrollTo({
        top: rect.top + scrollTop - 80,
        behavior: 'smooth',
      });
      shouldScroll.current = false;
    }
  }, [activeQuestion]);

  const allRoomsData = [
    {
      id: 0,
      questionKey: 'home.faq.all_rooms.0.question',
      answerKey: 'home.faq.all_rooms.0.answer',
    },
    {
      id: 1,
      questionKey: 'home.faq.all_rooms.1.question',
      answerKey: 'home.faq.all_rooms.1.answer',
    },
    {
      id: 2,
      questionKey: 'home.faq.all_rooms.2.question',
      answerKey: 'home.faq.all_rooms.2.answer',
    },
    {
      id: 3,
      questionKey: 'home.faq.all_rooms.3.question',
      answerKey: 'home.faq.all_rooms.3.answer',
    },
    {
      id: 4,
      questionKey: 'home.faq.all_rooms.4.question',
      answerKey: 'home.faq.all_rooms.4.answer',
    },
    {
      id: 5,
      questionKey: 'home.faq.all_rooms.5.question',
      answerKey: 'home.faq.all_rooms.5.answer',
    },
  ];

  const badiPlusData = [
    {
      id: 0,
      questionKey: 'home.faq.badi_plus.0.question',
      answerKey: 'home.faq.badi_plus.0.answer',
    },
    {
      id: 1,
      questionKey: 'home.faq.badi_plus.1.question',
      answerKey: 'home.faq.badi_plus.1.answer',
    },
    {
      id: 2,
      questionKey: 'home.faq.badi_plus.2.question',
      answerKey: 'home.faq.badi_plus.2.answer',
    },
    {
      id: 3,
      questionKey: 'home.faq.badi_plus.3.question',
      answerKey: 'home.faq.badi_plus.3.answer',
    },
    {
      id: 4,
      questionKey: 'home.faq.badi_plus.4.question',
      answerKey: 'home.faq.badi_plus.4.answer',
    },
    {
      id: 5,
      questionKey: 'home.faq.badi_plus.5.question',
      answerKey: 'home.faq.badi_plus.5.answer',
    },
    {
      id: 6,
      questionKey: 'home.faq.badi_plus.6.question',
      answerKey: 'home.faq.badi_plus.6.answer',
    },
    {
      id: 7,
      questionKey: 'home.faq.badi_plus.7.question',
      answerKey: 'home.faq.badi_plus.7.answer',
    },
    {
      id: 8,
      questionKey: 'home.faq.badi_plus.8.question',
      answerKey: 'home.faq.badi_plus.8.answer',
    },
    {
      id: 9,
      questionKey: 'home.faq.badi_plus.9.question',
      answerKey: 'home.faq.badi_plus.9.answer',
    },
    {
      id: 10,
      questionKey: 'home.faq.badi_plus.10.question',
      answerKey: 'home.faq.badi_plus.10.answer',
    },
    {
      id: 11,
      questionKey: 'home.faq.badi_plus.11.question',
      answerKey: 'home.faq.badi_plus.11.answer',
    },
  ];

  const faqData = mode === 'badi-plus' ? badiPlusData : allRoomsData;
  const activeColor =
    mode === 'badi-plus' ? 'linear-gradient(135deg, #FFE193 0%, #FFC55E 100%)' : '#374151';

  const formatAnswer = (answerText) => {
    // Split by newlines and format accordingly
    const lines = answerText.split('\n');
    const formattedLines = lines.map((line, index) => {
      if (line.startsWith('✅')) {
        return (
          <li key={index}>
            <strong>{line.substring(1).trim()}</strong>
          </li>
        );
      } else if (line.startsWith('•')) {
        return <li key={index}>{line.substring(1).trim()}</li>;
      } else if (line.match(/^\d+\./)) {
        return <li key={index}>{line.substring(line.indexOf('.') + 1).trim()}</li>;
      } else if (line.trim() === '') {
        return <br key={index} />;
      } else {
        return <p key={index}>{line}</p>;
      }
    });

    // Group consecutive list items
    const result = [];
    let currentList = null;
    let currentListType = null;

    formattedLines.forEach((line, index) => {
      if (line.type === 'li') {
        if (
          !currentList ||
          currentListType !==
            (line.props.children[0]?.props?.className === 'checkmark' ? 'ul' : 'ol')
        ) {
          if (currentList) {
            result.push(currentList);
          }
          currentListType = line.props.children[0]?.props?.className === 'checkmark' ? 'ul' : 'ol';
          currentList = React.createElement(currentListType, { key: `list-${index}` }, [line]);
        } else {
          currentList.props.children.push(line);
        }
      } else {
        if (currentList) {
          result.push(currentList);
          currentList = null;
          currentListType = null;
        }
        result.push(line);
      }
    });

    if (currentList) {
      result.push(currentList);
    }

    return <div>{result}</div>;
  };

  return (
    <Container>
      <MainContent ref={mainContentRef}>
        <Title>{translate('home.faq.title')}</Title>

        <FAQContainer>
          <QuestionsColumn>
            {faqData.map((faq) => (
              <QuestionButton
                key={faq.id}
                isActive={activeQuestion === faq.id}
                activeColor={activeColor}
                onClick={() => handleQuestionClick(faq.id)}
              >
                {translate(faq.questionKey)}
              </QuestionButton>
            ))}
          </QuestionsColumn>

          <AnswerColumn ref={answerRef}>
            {activeQuestion !== null ? (
              <AnswerContent>
                {formatAnswer(translate(faqData[activeQuestion]?.answerKey))}
              </AnswerContent>
            ) : (
              <EmptyState>Selecciona una pregunta para ver la respuesta</EmptyState>
            )}
          </AnswerColumn>
        </FAQContainer>
      </MainContent>
    </Container>
  );
}
